import React, { useEffect, useState, useCallback } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { thumbUrl } from "@/lib/utils-app";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PaintBrushIcon as PaintBrush, CheckCircleIcon as CheckCircle, HourglassIcon as Hourglass, XCircleIcon as XCircle, ArrowRightIcon as ArrowRight, PackageIcon as Package } from "@phosphor-icons/react";
import { NoiseOverlay, LuxuryCard } from "@/components/luxury";

/**
 * Public custom-order status page — `/armstrong/custom/:orderId`.
 *
 * Linked from:
 *   - the confirmation toast immediately after submitting the request
 *   - the email we send when the admin sends the mockup + quote
 *   - the Stripe success redirect (?status=paid)
 *
 * No auth required: the order_id itself is the unguessable token
 * (UUID4). Polled every 30s while open to pick up status changes.
 */
export default function ArmstrongCustomOrder() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [paying, setPaying] = useState(false);

  const justPaid = params.get("status") === "paid";

  const load = useCallback(() => {
    api.get(`/armstrong/custom/order/${orderId}`)
      .then((r) => setOrder(r.data))
      .catch((err) => { if (err?.response?.status === 404) setNotFound(true); });
  }, [orderId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (justPaid) {
      toast.success("Payment received — we'll get on your order right away.");
    }
  }, [justPaid]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/armstrong/custom/order/${orderId}/pay`, {
        origin_url: webOrigin(),
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout.");
      setPaying(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] p-6 text-center">
        <XCircle size={42} weight="duotone" className="text-[#9A9A9A] mb-4" />
        <h1 className="font-spectral text-2xl text-ink mb-2">Order not found</h1>
        <p className="text-[#6B6B6B] mb-6">This custom order link is invalid or has expired.</p>
        <Link to="/armstrong" className="text-gold-dark underline">Back to the storefront →</Link>
      </div>
    );
  }

  if (!order) {
    return <div className="min-h-screen flex items-center justify-center text-[#6B6B6B]">Loading…</div>;
  }

  const status = order.status;
  const canPay = status === "quoted" || status === "mockup_sent";
  const paid = status === "paid" || status === "fulfilled";
  const mockupSrc = order.mockup_url ? (thumbUrl(order.mockup_url) || order.mockup_url) : null;
  const total = (order.quoted_price_usd || 0) * (order.quantity || 1);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink font-manrope">
      <Helmet>
        <title>Your custom order — Armstrong K9 Registry</title>
      </Helmet>

      <header className="relative bg-ink text-white overflow-hidden" data-testid="custom-order-header">
        <NoiseOverlay opacity={0.08} />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(circle at 80% 20%, rgba(201,162,39,0.28), transparent 50%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-6 md:px-10 py-12">
          <Link to="/armstrong" className="text-[11px] text-white/60 hover:text-white uppercase tracking-[0.2em]">
            ← Back to storefront
          </Link>
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mt-4 mb-2 flex items-center gap-1.5">
            <PaintBrush size={12} weight="fill" /> Custom order
          </p>
          <h1 className="font-spectral font-light text-3xl md:text-5xl tracking-tight leading-[1.05]">
            {paid ? "All set — we got it" : status === "declined" ? "Update on your request" : "Your custom order"}
          </h1>
          <p className="mt-3 text-white/70 text-sm font-tabular">
            Order #{order.id.slice(0, 8).toUpperCase()}
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 space-y-6">
        <StatusPill status={status} />

        <LuxuryCard className="p-6" data-testid="custom-order-summary">
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark">
            Your request
          </p>
          <p className="text-[15px] text-[#3a3a3a] leading-relaxed mt-2 whitespace-pre-wrap">{order.description}</p>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Quantity</p>
              <p className="font-tabular text-[15px] mt-0.5">{order.quantity}</p>
            </div>
            {order.quoted_price_usd && (
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#9A9A9A]">Per-unit price</p>
                <p className="font-tabular text-[15px] mt-0.5">${order.quoted_price_usd.toFixed(2)}</p>
              </div>
            )}
          </div>
        </LuxuryCard>

        {mockupSrc && (
          <LuxuryCard className="p-6" data-testid="custom-order-mockup">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-gold-dark mb-3">
              The mockup we made for you
            </p>
            <img
              src={mockupSrc}
              alt="Custom mockup"
              className="w-full rounded-lg max-h-[480px] object-contain bg-cream"
            />
            {order.admin_notes && (
              <p className="text-[13px] text-[#3a3a3a] mt-4 leading-relaxed whitespace-pre-wrap">
                {order.admin_notes}
              </p>
            )}
          </LuxuryCard>
        )}

        {canPay && (
          <LuxuryCard className="p-6 bg-gradient-to-br from-[#FBF7EC] to-white border-gold-dark" data-testid="custom-order-pay-card">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-crimson">
              Approve and pay
            </p>
            <p className="font-spectral text-2xl text-ink mt-1">
              ${total.toFixed(2)} <span className="text-[#6B6B6B] text-base">total</span>
            </p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed">
              {order.quantity}× at ${(order.quoted_price_usd || 0).toFixed(2)} each. Payment is processed securely by Stripe.
            </p>
            <Button
              onClick={pay}
              disabled={paying}
              className="mt-5 rounded-full bg-ink hover:bg-crimson text-white h-12 px-6 font-semibold"
              data-testid="custom-order-pay-btn"
            >
              {paying ? "Opening checkout…" : `Pay $${total.toFixed(2)} →`}
            </Button>
            <p className="text-[11px] text-[#9A9A9A] mt-3 leading-relaxed">
              Once paid, we send the design to our print partner. Standard production + delivery is 7–10 business days.
            </p>
          </LuxuryCard>
        )}

        {status === "declined" && (
          <LuxuryCard className="p-6 border-crimson/30 bg-crimson/5" data-testid="custom-order-declined">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-crimson">
              We weren&rsquo;t able to take this one on
            </p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed whitespace-pre-wrap">
              {order.admin_notes || "Unfortunately we can't fulfill this particular request right now."}
            </p>
            <Link to="/armstrong" className="inline-block mt-4 text-gold-dark underline text-sm">
              Browse the storefront →
            </Link>
          </LuxuryCard>
        )}

        {paid && (
          <LuxuryCard className="p-6 bg-emerald-50 border-emerald-300" data-testid="custom-order-paid">
            <CheckCircle size={32} weight="duotone" className="text-emerald-700 mb-2" />
            <p className="font-spectral text-2xl text-ink">Thanks — we&rsquo;re on it.</p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed">
              {status === "fulfilled"
                ? "Your order has been fulfilled and shipped. Watch your email for tracking."
                : "Your payment is confirmed. We're submitting your design to our print partner next. You'll get an email with tracking once it ships."}
            </p>
          </LuxuryCard>
        )}

        {status === "requested" && !mockupSrc && (
          <LuxuryCard className="p-6 bg-cream/60" data-testid="custom-order-waiting">
            <Hourglass size={28} weight="duotone" className="text-gold-dark mb-2" />
            <p className="font-spectral text-xl text-ink">We&rsquo;ve got your request</p>
            <p className="text-[13px] text-[#3a3a3a] mt-2 leading-relaxed">
              We&rsquo;ll review and send you a mockup within 5 business days. You&rsquo;ll get an email
              and this page will update automatically. Save the link — it&rsquo;s your home base
              for this order.
            </p>
          </LuxuryCard>
        )}

        <div className="pt-4 text-center">
          <Link to="/armstrong" className="inline-flex items-center gap-1.5 text-[12px] text-[#6B6B6B] hover:text-ink">
            <Package size={12} weight="duotone" /> Back to the storefront <ArrowRight size={11} />
          </Link>
        </div>
      </main>
    </div>
  );
}

const STATUS_META = {
  requested: { label: "Awaiting mockup", cls: "bg-amber-100 text-amber-900 border-amber-300", testid: "status-requested" },
  mockup_sent: { label: "Mockup ready — review and pay", cls: "bg-[#FBF7EC] text-ink border-gold-dark", testid: "status-mockup-sent" },
  quoted: { label: "Mockup ready — review and pay", cls: "bg-[#FBF7EC] text-ink border-gold-dark", testid: "status-quoted" },
  paid: { label: "Paid — in production", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", testid: "status-paid" },
  fulfilled: { label: "Fulfilled", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", testid: "status-fulfilled" },
  declined: { label: "Unable to fulfill", cls: "bg-crimson/10 text-crimson border-crimson/30", testid: "status-declined" },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "bg-gray-100 text-gray-700 border-gray-300", testid: "status-other" };
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.18em] rounded-full px-3 py-1 border ${meta.cls}`}
      data-testid={meta.testid}
    >
      {meta.label}
    </div>
  );
}
